import type { OpeningStatus } from '../../../lib/api/domains/customerCare';

/**
 * Status → Farbe und Satz. Die Uhrzeiten stehen in der Zeitzone des KUNDEN — „öffnet
 * 08:00" heißt 08:00 bei ihm, nicht beim Techniker.
 */

/** Unter dieser Schwelle wird „geöffnet" zu „schließt gleich". */
export const CLOSING_SOON_MINUTES = 30;

function dateKey(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

function hhmm(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('de-DE', { timeZone, hour: '2-digit', minute: '2-digit' }).format(d);
}

/** „08:00", „morgen 08:00", „Mo 08:00", „Mo 19.10. 08:00" — je nachdem, wie weit weg. */
export function formatWhen(iso: string, timeZone: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const today = dateKey(now, timeZone);
  const target = dateKey(d, timeZone);
  if (target === today) return hhmm(d, timeZone);
  const tomorrow = dateKey(new Date(now.getTime() + 24 * 3600_000), timeZone);
  if (target === tomorrow) return `morgen ${hhmm(d, timeZone)}`;
  const days = (d.getTime() - now.getTime()) / (24 * 3600_000);
  const weekday = new Intl.DateTimeFormat('de-DE', { timeZone, weekday: 'short' }).format(d).replace('.', '');
  if (days < 6) return `${weekday} ${hhmm(d, timeZone)}`;
  const date = new Intl.DateTimeFormat('de-DE', { timeZone, day: '2-digit', month: '2-digit' }).format(d);
  return `${weekday} ${date} ${hhmm(d, timeZone)}`;
}

/** „in 25 Min", „in 2 Std" — nur für die Nähe; weiter weg sagt die Uhrzeit mehr. */
export function formatIn(iso: string, now: Date = new Date()): string | null {
  const minutes = Math.round((new Date(iso).getTime() - now.getTime()) / 60_000);
  if (minutes < 0) return null;
  if (minutes < 60) return `in ${Math.max(1, minutes)} Min`;
  if (minutes < 6 * 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `in ${h} Std ${m} Min` : `in ${h} Std`;
  }
  return null;
}

function formatDate(key: string): string {
  const [y, m, d] = key.split('-');
  return `${d}.${m}.${y.slice(2)}`;
}

export interface OpeningStatusView {
  color: string;
  /** Kurz, für das Badge: „Geöffnet bis 12:00" */
  label: string;
  /** Ergänzung: „schließt in 20 Min", „öffnet Mo 08:00" */
  detail: string | null;
}

export function describeStatus(status: OpeningStatus, now: Date = new Date()): OpeningStatusView {
  const tz = status.timeZone;
  switch (status.state) {
    case 'UNKNOWN':
      return { color: 'gray', label: 'Öffnungszeiten unbekannt', detail: null };
    case 'OPEN': {
      if (!status.until) return { color: 'green', label: 'Geöffnet', detail: null };
      const left = (new Date(status.until).getTime() - now.getTime()) / 60_000;
      const soon = left <= CLOSING_SOON_MINUTES;
      return {
        color: soon ? 'orange' : 'green',
        label: `Geöffnet bis ${formatWhen(status.until, tz, now)}`,
        detail: soon ? `schließt ${formatIn(status.until, now) ?? 'gleich'}` : null,
      };
    }
    case 'BREAK':
      return {
        color: 'yellow',
        label: status.nextOpen ? `Pause bis ${formatWhen(status.nextOpen, tz, now)}` : 'Pause',
        detail: status.nextOpen ? formatIn(status.nextOpen, now) : null,
      };
    case 'HOLIDAY':
      return {
        color: 'red',
        label: 'Feiertag – geschlossen',
        detail: status.nextOpen ? `öffnet ${formatWhen(status.nextOpen, tz, now)}` : null,
      };
    case 'VACATION':
      return {
        color: 'red',
        label: `${status.closureLabel || 'Betriebsurlaub'}${status.closureUntil ? ` bis ${formatDate(status.closureUntil)}` : ''}`,
        detail: status.nextOpen ? `öffnet ${formatWhen(status.nextOpen, tz, now)}` : null,
      };
    case 'CLOSED':
      return {
        color: 'red',
        label: 'Geschlossen',
        detail: status.nextOpen
          ? `öffnet ${formatWhen(status.nextOpen, tz, now)}${formatIn(status.nextOpen, now) ? ` (${formatIn(status.nextOpen, now)})` : ''}`
          : null,
      };
  }
}

/** Alle Abzeichen eines Kunden teilen sich einen Schlüssel — gespeichert wird per Präfix invalidiert. */
export const OPENING_STATUS_QUERY_KEY = 'customer-opening-status';
